import { ArrowDown, Scale, Bluetooth, BatteryCharging, Usb, Waves } from "lucide-react";
import { Reveal, Eyebrow, SectionTitle } from "../Reveal";

const FLOW = ["Pièces", "Mesure", "Estimation", "Application", "Alerte"];

const TECH = [
  { icon: Scale, label: "Mesure du poids par compartiment" },
  { icon: Bluetooth, label: "Bluetooth Low Energy" },
  { icon: BatteryCharging, label: "Batterie rechargeable" },
  { icon: Usb, label: "Recharge USB-C" },
  { icon: Waves, label: "Filtrage et stabilisation des mesures" },
];

export default function HowItWorks() {
  return (
    <>
      <section id="fonctionnement" className="py-16 sm:py-24" data-testid="howitworks-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-12 items-center">
          <Reveal>
            <Eyebrow>Fonctionnement</Eyebrow>
            <SectionTitle>Un principe simple.<br />Un suivi continu.</SectionTitle>
            <p className="mt-6 text-gray-400 leading-relaxed max-w-lg">
              Le concept STOCKBOX 8 repose sur une chaîne simple : vos pièces sont pesées,
              la quantité restante est estimée, puis affichée dans l'application.
              Vous êtes alerté lorsqu'un niveau devient faible.
            </p>
            <p className="mt-4 text-sm text-gray-500 max-w-lg">
              Les quantités affichées sont toujours des <strong className="text-gray-300">estimations</strong> —
              jamais un comptage exact à l'unité près.
            </p>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="border border-[#2E333D] bg-[#141619] rounded-2xl p-6 sm:p-8" data-testid="flow-diagram">
              {FLOW.map((step, i) => (
                <div key={step}>
                  <div className={`flex items-center justify-between border rounded-xl px-5 py-4 ${i === FLOW.length - 1 ? "border-[#FFC700] bg-[#FFC700]/10" : "border-[#2E333D] bg-[#0C0D0E]"}`}>
                    <span className={`font-display font-bold uppercase tracking-wider text-lg ${i === FLOW.length - 1 ? "text-[#FFC700]" : "text-[#F9FAFB]"}`}>{step}</span>
                    <span className="font-mono text-[10px] text-gray-600">0{i + 1}</span>
                  </div>
                  {i < FLOW.length - 1 && (
                    <div className="flex justify-center py-1.5">
                      <ArrowDown size={16} className="text-[#FFC700]/60" />
                    </div>
                  )}
                </div>
              ))}
              <div className="mt-6 border border-dashed border-[#FFC700]/40 rounded-xl px-5 py-4 text-center" data-testid="flow-example">
                <span className="font-mono text-xs uppercase tracking-widest text-gray-500">Exemple d'affichage</span>
                <p className="font-display text-2xl font-bold text-[#FFC700] mt-1">« Il reste environ 37 pièces. »</p>
                <p className="text-xs text-gray-500 mt-1">Quantité estimée — valeur d'illustration</p>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      <section id="technologie" className="py-16 sm:py-24 bg-[#141619]/40 border-y border-[#2E333D]/50" data-testid="technology-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal>
            <Eyebrow>Technologie envisagée</Eyebrow>
            <SectionTitle>Une mesure indépendante pour chacun des 8 compartiments.</SectionTitle>
          </Reveal>
          <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-5 gap-4">
            {TECH.map((t, i) => (
              <Reveal key={t.label} delay={i * 0.07}>
                <div className="h-full border border-[#2E333D] bg-[#0C0D0E] rounded-xl p-5 hover:border-[#FFC700]/40 transition-colors" data-testid={`tech-item-${i}`}>
                  <t.icon size={20} className="text-[#FFC700] mb-3" />
                  <p className="text-sm text-gray-300 font-medium leading-snug">{t.label}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.3}>
            <p className="mt-8 font-mono text-xs uppercase tracking-widest text-gray-500" data-testid="tech-disclaimer">
              Technologie en cours de développement — performances non définitivement validées.
            </p>
          </Reveal>
        </div>
      </section>
    </>
  );
}
